class AudioService {
  // Audio-graph nodes are null until startCapture() builds the chain and
  // null again after stopCapture(). They're typed `any` rather than
  // `<Node> | null` because the setup methods thread them through ~40
  // call sites that would each need a non-null assertion.
  audioStream: MediaStream | null;
  audioContext: any;
  processor: any;
  source: any;
  isRecording: boolean;
  // Set by stopCapture(). startCapture() re-checks it after each await so a
  // stop issued while getUserMedia / worklet setup is still pending aborts
  // the start instead of leaving a live mic nobody owns.
  stopRequested: boolean;
  onDataCallback: ((data: ArrayBuffer) => void) | null;
  lastFrameAt: number;
  onAudioContextStateChange: ((state: string) => void) | null;
  silentOutputGain: any;
  echoCancellationNode: any;
  noiseSuppressNode: any;
  compressorNode: any;
  filterNode: any;
  gainNode: any;
  analyzerNode: any;
  limiterNode: any;
  vadThreshold: number;
  vadSmoothingFactor: number;
  currentVoiceLevel: number;
  voiceActivityCallback: ((active: boolean, level: number) => void) | null;
  lastVoiceActivity: boolean;
  echoCancellationEnabled: boolean;
  adaptiveFilterLength: number;
  echoBuffer: Float32Array;
  echoBufferIndex: number;
  audioQualityMetrics: {
    averageLevel: number;
    peakLevel: number;
    dynamicRange: number;
    signalToNoise: number;
    clippingEvents: number;
  };
  adaptiveGainEnabled: boolean;
  targetLevel: number;
  gainAdjustmentSpeed: number;
  currentGain: number;
  noiseProfile: Float32Array;
  noiseProfileLearned: boolean;
  noiseLearningSamples: number;
  noiseThreshold: number;

  constructor() {
    this.audioStream = null;
    this.audioContext = null;
    this.processor = null;
    this.source = null;
    this.isRecording = false;
    this.stopRequested = false;
    this.onDataCallback = null;

    // Timestamp of the last audio frame successfully handed to the caller.
    // Used by session managers to detect stalls when the tab is backgrounded.
    this.lastFrameAt = 0;

    // Notified whenever the underlying AudioContext changes state (e.g. the
    // browser suspended it because the tab was put to sleep). The callback
    // receives the new state string ('running' | 'suspended' | 'closed' |
    // 'interrupted'). The scribe session manager subscribes here so it can
    // flip its connection-status banner to STALLED the moment the OS
    // suspends capture, rather than waiting for the setInterval-based stall
    // detector to notice — that detector is itself throttled to ≤1 Hz when
    // the renderer is suspended, so it's too slow to be the primary signal.
    this.onAudioContextStateChange = null;

    // Silent output node — keeps the tab classified as "playing media" so
    // Edge Sleeping Tabs and Chromium intensive wake-up throttling leave the
    // AudioContext alone. Gain=0 means no sound actually reaches the speakers.
    // This trick used to be reliable, but Chromium / Edge 148 made the
    // background-tab heuristic stricter and a muted destination is no longer
    // a guaranteed exemption — so the session manager backs this up with a
    // `visibilitychange` listener that force-resumes the context on tab
    // return.
    this.silentOutputGain = null;

    // Enhanced audio processing nodes
    this.echoCancellationNode = null;
    this.noiseSuppressNode = null;
    this.compressorNode = null;
    this.filterNode = null;
    this.gainNode = null;
    this.analyzerNode = null;
    this.limiterNode = null;

    // Voice Activity Detection
    this.vadThreshold = -50; // dB threshold for voice detection
    this.vadSmoothingFactor = 0.8;
    this.currentVoiceLevel = -100;
    this.voiceActivityCallback = null;
    this.lastVoiceActivity = false;

    // Echo cancellation state
    this.echoCancellationEnabled = true;
    this.adaptiveFilterLength = 1024;
    this.echoBuffer = new Float32Array(this.adaptiveFilterLength);
    this.echoBufferIndex = 0;

    // Audio quality monitoring
    this.audioQualityMetrics = {
      averageLevel: 0,
      peakLevel: 0,
      dynamicRange: 0,
      signalToNoise: 0,
      clippingEvents: 0,
    };

    // Adaptive parameters
    this.adaptiveGainEnabled = true;
    this.targetLevel = -20; // dB
    this.gainAdjustmentSpeed = 0.1;
    this.currentGain = 1.0;

    // Background noise profile
    this.noiseProfile = new Float32Array(1024);
    this.noiseProfileLearned = false;
    this.noiseLearningSamples = 0;
    this.noiseThreshold = 0.01;
  }

  async startCapture(
    onDataCallback: (data: ArrayBuffer) => void,
    options: Record<string, any> = {},
  ): Promise<boolean> {
    this.stopRequested = false;
    try {
      // Enhanced options for group AI encounters
      const defaultOptions = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        voiceActivityDetection: true,
        adaptiveGainControl: true,
        backgroundNoiseSuppression: true,
        channelCount: 1,
        sampleRate: 16000,
        // Advanced constraints for better group conversation handling
        latency: 0.01, // 10ms latency for real-time processing
        deviceId: options.deviceId || undefined,
      };

      const audioOptions = {...defaultOptions, ...options};

      // Request microphone access with enhanced constraints
      // The goog* constraints are Chromium-only extensions absent from the
      // standard MediaTrackConstraints type.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: audioOptions.echoCancellation,
          noiseSuppression: audioOptions.noiseSuppression,
          autoGainControl: audioOptions.autoGainControl,
          channelCount: audioOptions.channelCount,
          sampleRate: audioOptions.sampleRate,
          latency: audioOptions.latency,
          deviceId: audioOptions.deviceId,
          // Advanced constraints for group conversations
          googEchoCancellation: true,
          googAutoGainControl: true,
          googNoiseSuppression: true,
          googHighpassFilter: true,
          googTypingNoiseDetection: true,
          googAudioMirroring: false,
          // Experimental features for better group audio
          googExperimentalEchoCancellation: true,
          googExperimentalAutoGainControl: true,
          googExperimentalNoiseSuppression: true,
        } as any,
      });

      // stopCapture() may have run while the permission prompt / device
      // warm-up was pending (dictation cancelled or its connect watchdog
      // fired). At that point there was nothing on `this` for stopCapture to
      // release, so stop the tracks here — otherwise the mic stays live (and
      // its browser indicator on) with no owner.
      if (this.stopRequested) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error(
          'Audio capture was stopped before the microphone was ready',
        );
      }
      this.audioStream = stream;

      this.onDataCallback = onDataCallback;
      this.voiceActivityCallback = options.onVoiceActivity || null;

      // Create audio context with optimized settings
      this.audioContext = new AudioContext({
        sampleRate: audioOptions.sampleRate,
        latencyHint: 'interactive',
      });

      // Forward AudioContext state transitions to the caller. Browsers fire
      // `statechange` when they suspend the context for tab inactivity, when
      // the OS interrupts audio (phone call, switch user, etc.), and when
      // the context is closed. The scribe session manager uses this to flip
      // its banner to STALLED immediately on suspension, instead of waiting
      // for the throttled stall-detector setInterval.
      this.audioContext.addEventListener?.('statechange', () => {
        if (
          this.audioContext &&
          typeof this.onAudioContextStateChange === 'function'
        ) {
          try {
            this.onAudioContextStateChange(this.audioContext.state);
          } catch {
            /* ignore listener failures */
          }
        }
      });

      this.onAudioContextStateChange =
        options.onAudioContextStateChange || null;

      // Set up advanced audio processing chain
      await this.setupAdvancedAudioProcessing(audioOptions);

      // stopCapture() may also have run during the worklet setup above; it
      // already released whatever nodes existed at that moment, so run it
      // once more to release the rest and report the start as aborted.
      if (this.stopRequested) {
        this.stopCapture();
        throw new Error(
          'Audio capture was stopped before the microphone was ready',
        );
      }

      this.isRecording = true;
      // Audio capture active

      return true;
    } catch (error) {
      if (!this.stopRequested) {
        console.error('Error starting enhanced audio capture:', error);
      }
      throw error;
    }
  }

  async setupAdvancedAudioProcessing(options: Record<string, any>) {
    // Create media stream source
    this.source = this.audioContext.createMediaStreamSource(this.audioStream);

    // 1. Pre-filter: High-pass filter to remove low-frequency noise
    this.filterNode = this.audioContext.createBiquadFilter();
    this.filterNode.type = 'highpass';
    this.filterNode.frequency.setValueAtTime(80, this.audioContext.currentTime); // Remove rumble/hum
    this.filterNode.Q.setValueAtTime(0.7, this.audioContext.currentTime);

    // 2. Compressor for dynamic range control
    this.compressorNode = this.audioContext.createDynamicsCompressor();
    this.compressorNode.threshold.setValueAtTime(
      -24,
      this.audioContext.currentTime,
    );
    this.compressorNode.knee.setValueAtTime(30, this.audioContext.currentTime);
    this.compressorNode.ratio.setValueAtTime(12, this.audioContext.currentTime);
    this.compressorNode.attack.setValueAtTime(
      0.003,
      this.audioContext.currentTime,
    );
    this.compressorNode.release.setValueAtTime(
      0.25,
      this.audioContext.currentTime,
    );

    // 3. Adaptive gain control
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.setValueAtTime(
      this.currentGain,
      this.audioContext.currentTime,
    );

    // 4. Analyzer for voice activity detection and quality monitoring
    this.analyzerNode = this.audioContext.createAnalyser();
    this.analyzerNode.fftSize = 2048;
    this.analyzerNode.smoothingTimeConstant = 0.8;

    // 5. Limiter to prevent clipping
    this.limiterNode = this.audioContext.createDynamicsCompressor();
    this.limiterNode.threshold.setValueAtTime(
      -3,
      this.audioContext.currentTime,
    );
    this.limiterNode.knee.setValueAtTime(0, this.audioContext.currentTime);
    this.limiterNode.ratio.setValueAtTime(20, this.audioContext.currentTime);
    this.limiterNode.attack.setValueAtTime(
      0.001,
      this.audioContext.currentTime,
    );
    this.limiterNode.release.setValueAtTime(
      0.01,
      this.audioContext.currentTime,
    );

    // Connect the processing chain
    this.source
      .connect(this.filterNode)
      .connect(this.compressorNode)
      .connect(this.gainNode)
      .connect(this.analyzerNode)
      .connect(this.limiterNode);

    // Silent tap to destination. Muted (gain=0) so no audio is heard, but
    // routing to the destination makes the browser treat the tab as "playing
    // audio". That exempts it from Edge Sleeping Tabs and from Chromium's
    // intensive background throttling — which are the main reasons a Scribe
    // session silently stops capturing when the tab is occluded for minutes.
    this.silentOutputGain = this.audioContext.createGain();
    this.silentOutputGain.gain.setValueAtTime(0, this.audioContext.currentTime);
    this.limiterNode.connect(this.silentOutputGain);
    this.silentOutputGain.connect(this.audioContext.destination);

    // Set up enhanced PCM processor
    if (this.audioContext.audioWorklet) {
      try {
        await this.setupEnhancedAudioWorklet(options);
      } catch (error) {
        console.warn(
          'Enhanced AudioWorklet failed, falling back to ScriptProcessor:',
          error,
        );
        this.setupEnhancedScriptProcessor(options);
      }
    } else {
      this.setupEnhancedScriptProcessor(options);
    }

    // Start voice activity detection and quality monitoring
    this.startVoiceActivityDetection();
    this.startAudioQualityMonitoring();
  }

  async setupEnhancedAudioWorklet(options: Record<string, any>) {
    const workletCode = `
      class EnhancedPCMProcessor extends AudioWorkletProcessor {
        constructor() {
          super();

          // Voice Activity Detection
          this.vadThreshold = -50;
          this.vadSmoothingFactor = 0.8;
          this.currentVoiceLevel = -100;
          this.lastVoiceActivity = false;

          // Noise suppression
          this.noiseProfile = new Float32Array(128);
          this.noiseGate = 0.01;
          this.noiseReduction = 0.8;

          // Echo cancellation buffer
          this.echoBuffer = new Float32Array(1024);
          this.echoIndex = 0;

          // Adaptive gain
          this.targetLevel = -20;
          this.currentGain = 1.0;
          this.gainAdjustmentSpeed = 0.001;

          // Quality metrics
          this.peakLevel = 0;
          this.averageLevel = 0;
          this.clippingCount = 0;
          this.sampleCount = 0;
        }

        process(inputs, outputs, parameters) {
          const input = inputs[0];
          if (input.length > 0) {
            const audioData = input[0];
            const processedData = this.processAudioData(audioData);

            // Convert Float32 to Int16 PCM
            const pcmData = new Int16Array(processedData.length);
            for (let i = 0; i < processedData.length; i++) {
              pcmData[i] = Math.max(-32768, Math.min(32767, processedData[i] * 32768));

              // Track clipping
              if (Math.abs(pcmData[i]) >= 32767) {
                this.clippingCount++;
              }
            }

            // Send PCM data and voice activity info to main thread
            this.port.postMessage({
              audioData: pcmData.buffer,
              voiceActivity: this.lastVoiceActivity,
              voiceLevel: this.currentVoiceLevel,
              peakLevel: this.peakLevel,
              clippingEvents: this.clippingCount
            });

            this.clippingCount = 0; // Reset clipping counter
          }
          return true;
        }

        processAudioData(audioData) {
          const processed = new Float32Array(audioData.length);

          for (let i = 0; i < audioData.length; i++) {
            let sample = audioData[i];

            // 1. Noise gate
            if (Math.abs(sample) < this.noiseGate) {
              sample *= this.noiseReduction;
            }

            // 2. Adaptive gain control
            const level = 20 * Math.log10(Math.abs(sample) + 1e-10);
            this.currentVoiceLevel = this.vadSmoothingFactor * this.currentVoiceLevel +
                                   (1 - this.vadSmoothingFactor) * level;

            if (this.currentVoiceLevel > this.vadThreshold) {
              this.lastVoiceActivity = true;

              // Adjust gain to reach target level
              const gainError = this.targetLevel - this.currentVoiceLevel;
              const gainAdjustment = gainError * this.gainAdjustmentSpeed;
              this.currentGain = Math.max(0.1, Math.min(4.0, this.currentGain + gainAdjustment));
            } else {
              this.lastVoiceActivity = false;
            }

            // Apply adaptive gain
            sample *= this.currentGain;

            // 3. Update echo cancellation buffer
            this.echoBuffer[this.echoIndex] = sample;
            this.echoIndex = (this.echoIndex + 1) % this.echoBuffer.length;

            // 4. Soft limiting
            if (Math.abs(sample) > 0.95) {
              sample = Math.sign(sample) * (0.95 + 0.05 * Math.tanh((Math.abs(sample) - 0.95) * 10));
            }

            processed[i] = sample;

            // Update quality metrics
            this.peakLevel = Math.max(this.peakLevel, Math.abs(sample));
            this.averageLevel += Math.abs(sample);
            this.sampleCount++;
          }

          // Decay peak level
          this.peakLevel *= 0.999;

          return processed;
        }
      }
      registerProcessor('enhanced-pcm-processor', EnhancedPCMProcessor);
    `;

    const blob = new Blob([workletCode], {type: 'application/javascript'});
    const workletUrl = URL.createObjectURL(blob);

    await this.audioContext.audioWorklet.addModule(workletUrl);

    this.processor = new AudioWorkletNode(
      this.audioContext,
      'enhanced-pcm-processor',
    );
    this.processor.port.onmessage = (event: MessageEvent) => {
      const {audioData, voiceActivity, voiceLevel, peakLevel, clippingEvents} =
        event.data;

      // Update voice activity
      if (
        this.lastVoiceActivity !== voiceActivity &&
        this.voiceActivityCallback
      ) {
        this.voiceActivityCallback(voiceActivity, voiceLevel);
      }
      this.lastVoiceActivity = voiceActivity;
      this.currentVoiceLevel = voiceLevel;

      // Update quality metrics
      this.audioQualityMetrics.peakLevel = peakLevel;
      this.audioQualityMetrics.clippingEvents += clippingEvents;

      // Send processed audio data
      if (this.onDataCallback) {
        this.lastFrameAt = Date.now();
        this.onDataCallback(audioData);
      }
    };

    this.limiterNode.connect(this.processor);

    // Clean up the blob URL
    URL.revokeObjectURL(workletUrl);
  }

  setupEnhancedScriptProcessor(options: Record<string, any>) {
    // Enhanced fallback to ScriptProcessor for older browsers
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);

    this.processor.onaudioprocess = (event: AudioProcessingEvent) => {
      const audioData = event.inputBuffer.getChannelData(0);
      const processed = this.processAudioDataFallback(audioData);

      // Convert Float32 to Int16 PCM
      const pcmData = new Int16Array(processed.length);
      for (let i = 0; i < processed.length; i++) {
        pcmData[i] = Math.max(-32768, Math.min(32767, processed[i] * 32768));
      }

      // Send processed audio data
      if (this.onDataCallback) {
        this.lastFrameAt = Date.now();
        this.onDataCallback(pcmData.buffer);
      }
    };

    this.limiterNode.connect(this.processor);
    this.processor.connect(this.audioContext.destination);
  }

  processAudioDataFallback(audioData: Float32Array): Float32Array {
    const processed = new Float32Array(audioData.length);

    for (let i = 0; i < audioData.length; i++) {
      let sample = audioData[i];

      // Basic noise gate
      if (Math.abs(sample) < this.noiseThreshold) {
        sample *= 0.1;
      }

      // Voice activity detection
      const level = 20 * Math.log10(Math.abs(sample) + 1e-10);
      this.currentVoiceLevel =
        this.vadSmoothingFactor * this.currentVoiceLevel +
        (1 - this.vadSmoothingFactor) * level;

      const voiceActivity = this.currentVoiceLevel > this.vadThreshold;
      if (
        this.lastVoiceActivity !== voiceActivity &&
        this.voiceActivityCallback
      ) {
        this.voiceActivityCallback(voiceActivity, this.currentVoiceLevel);
      }
      this.lastVoiceActivity = voiceActivity;

      // Adaptive gain
      if (voiceActivity && this.adaptiveGainEnabled) {
        const gainError = this.targetLevel - this.currentVoiceLevel;
        const gainAdjustment = gainError * this.gainAdjustmentSpeed;
        this.currentGain = Math.max(
          0.1,
          Math.min(4.0, this.currentGain + gainAdjustment),
        );
        sample *= this.currentGain;
      }

      processed[i] = sample;
    }

    return processed;
  }

  startVoiceActivityDetection() {
    // Additional voice activity monitoring using analyzer node
    const bufferLength = this.analyzerNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const checkVoiceActivity = () => {
      if (!this.isRecording) return;

      this.analyzerNode.getByteFrequencyData(dataArray);

      // Calculate voice activity based on frequency analysis
      let voiceEnergySum = 0;
      const voiceFreqStart = Math.floor(
        (300 * bufferLength) / (this.audioContext.sampleRate / 2),
      ); // 300Hz
      const voiceFreqEnd = Math.floor(
        (3400 * bufferLength) / (this.audioContext.sampleRate / 2),
      ); // 3400Hz

      for (let i = voiceFreqStart; i < voiceFreqEnd; i++) {
        voiceEnergySum += dataArray[i];
      }

      const averageVoiceEnergy =
        voiceEnergySum / (voiceFreqEnd - voiceFreqStart);
      const voiceActivityLevel =
        20 * Math.log10(averageVoiceEnergy / 255 + 1e-10);

      // Update voice activity state
      const isVoiceActive = voiceActivityLevel > this.vadThreshold;
      if (
        this.lastVoiceActivity !== isVoiceActive &&
        this.voiceActivityCallback
      ) {
        this.voiceActivityCallback(isVoiceActive, voiceActivityLevel);
      }

      requestAnimationFrame(checkVoiceActivity);
    };

    checkVoiceActivity();
  }

  startAudioQualityMonitoring() {
    const bufferLength = this.analyzerNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const timeDataArray = new Uint8Array(bufferLength);

    const updateQualityMetrics = () => {
      if (!this.isRecording) return;

      this.analyzerNode.getByteFrequencyData(dataArray);
      this.analyzerNode.getByteTimeDomainData(timeDataArray);

      // Calculate signal-to-noise ratio
      let signalEnergy = 0;
      let noiseEnergy = 0;

      for (let i = 0; i < bufferLength; i++) {
        const freqValue = dataArray[i] / 255;
        const timeValue = (timeDataArray[i] - 128) / 128;

        if (freqValue > 0.1) {
          // Signal
          signalEnergy += freqValue * freqValue;
        } else {
          // Noise
          noiseEnergy += freqValue * freqValue;
        }

        // Update average level
        this.audioQualityMetrics.averageLevel =
          0.95 * this.audioQualityMetrics.averageLevel +
          0.05 * Math.abs(timeValue);
      }

      this.audioQualityMetrics.signalToNoise =
        signalEnergy > 0 && noiseEnergy > 0
          ? 10 * Math.log10(signalEnergy / noiseEnergy)
          : 0;

      this.audioQualityMetrics.dynamicRange =
        this.audioQualityMetrics.peakLevel -
        this.audioQualityMetrics.averageLevel;

      setTimeout(updateQualityMetrics, 100); // Update every 100ms
    };

    updateQualityMetrics();
  }

  stopCapture() {
    this.stopRequested = true;
    this.isRecording = false;
    this.lastFrameAt = 0;

    if (this.silentOutputGain) {
      try {
        this.silentOutputGain.disconnect();
      } catch {
        /* noop */
      }
      this.silentOutputGain = null;
    }

    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.limiterNode) {
      this.limiterNode.disconnect();
      this.limiterNode = null;
    }

    if (this.analyzerNode) {
      this.analyzerNode.disconnect();
      this.analyzerNode = null;
    }

    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    if (this.compressorNode) {
      this.compressorNode.disconnect();
      this.compressorNode = null;
    }

    if (this.filterNode) {
      this.filterNode.disconnect();
      this.filterNode = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }

    this.onDataCallback = null;
    this.voiceActivityCallback = null;
  }

  /**
   * Force-resume the underlying AudioContext if it has been suspended by
   * the browser (Edge Sleeping Tabs, Chrome Memory Saver, OS audio
   * interruption, etc.). Idempotent and safe to call when already running.
   *
   * Returns `true` if the context is `running` after the call. Callers can
   * use the return value to decide whether to clear a stall banner.
   */
  async resumeContext() {
    const ctx = this.audioContext;
    if (!ctx) return false;
    if (ctx.state === 'closed') return false;
    if (ctx.state === 'running') return true;
    try {
      await ctx.resume();
    } catch {
      // resume() rejects when there's no user-gesture context (rare for
      // active Scribe sessions because the user clicked Start), or when
      // the device's audio session is genuinely held by another app. We
      // surface the post-call state below so the caller can decide.
    }
    return ctx.state === 'running';
  }

  // New methods for enhanced functionality

  getAudioQualityMetrics() {
    return {...this.audioQualityMetrics};
  }

  setVoiceActivityThreshold(threshold: number) {
    this.vadThreshold = threshold;
  }

  setAdaptiveGainTarget(targetLevel: number) {
    this.targetLevel = targetLevel;
  }

  toggleEchoCancellation(enabled: boolean) {
    this.echoCancellationEnabled = enabled;
    console.log(`Echo cancellation ${enabled ? 'enabled' : 'disabled'}`);
  }

  adjustGain(gain: number) {
    if (this.gainNode) {
      this.currentGain = Math.max(0.1, Math.min(4.0, gain));
      this.gainNode.gain.setValueAtTime(
        this.currentGain,
        this.audioContext.currentTime,
      );
    }
  }

  async getAudioDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.substr(0, 8)}`,
          groupId: device.groupId,
        }));
    } catch (error) {
      console.error('Error enumerating audio devices:', error);
      return [];
    }
  }

  // Test audio processing quality
  async testAudioQuality(duration: number = 5000): Promise<any> {
    console.log('🧪 Starting audio quality test...');

    const startTime = Date.now();
    const initialMetrics = {...this.audioQualityMetrics};

    return new Promise(resolve => {
      setTimeout(() => {
        const endTime = Date.now();
        const finalMetrics = {...this.audioQualityMetrics};

        const testResults = {
          duration: endTime - startTime,
          averageLevelImprovement:
            finalMetrics.averageLevel - initialMetrics.averageLevel,
          signalToNoiseRatio: finalMetrics.signalToNoise,
          clippingEventsPerSecond:
            finalMetrics.clippingEvents / (duration / 1000),
          dynamicRange: finalMetrics.dynamicRange,
          overall: 'good', // Simple classification
        };

        if (
          testResults.signalToNoiseRatio > 20 &&
          testResults.clippingEventsPerSecond < 1
        ) {
          testResults.overall = 'excellent';
        } else if (testResults.signalToNoiseRatio > 10) {
          testResults.overall = 'good';
        } else {
          testResults.overall = 'needs improvement';
        }

        console.log('🧪 Audio quality test results:', testResults);
        resolve(testResults);
      }, duration);
    });
  }
}

export default AudioService;
